import { Space } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiService, Request } from "../../api";
import DynamicFilterForm, {
  FilterValues,
} from "../../components/DynamicFilterForm";
import { exportRequestsToExcel } from "../../utils/exportRequests";
import { usePagination } from "../../hooks/usePagination";
import ManageableTable, {
  ManageableColumn,
} from "../../components/ManageableTable";
import { useRefreshOnTabFocus } from "../../tabs/tabContext";
import { PageHeader, useHeaderSlot } from "../../components/PageHeader";

export const ListPage = () => {
  const { t } = useTranslation();
  const columns: ManageableColumn<Request>[] = [
    {
      key: "id",
      title: t("ID"),
      dataIndex: "id",
      width: 80,
    },
    {
      key: "number",
      title: t("Number"),
      dataIndex: "number",
      width: 160,
    },
    {
      key: "template",
      title: t("Template"),
      width: 140,
      render: (_, record) => (
        <Space size="middle">{record.template?.short_name}</Space>
      ),
    },
    {
      key: "description",
      title: t("Description"),
      dataIndex: "description",
      width: 300,
    },
    {
      key: "actions",
      title: t("Actions"),
      width: 100,
      hideable: false,
      render: (_, record) => (
        <Space size="middle">
          <a href={`/requests/${record.id}`}>{t("View")}</a>
        </Space>
      ),
    },
  ];
  const [data, setData] = useState<Array<Request>>([]);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState<number>(0);
  const [filterValues, setFilterValues] = useState<FilterValues>({});
  const { page, pageSize, total, applyResponse, resetPage, paginationProps } =
    usePagination();
  const { slot, target } = useHeaderSlot();
  const filtered = Object.keys(filterValues).length > 0;

  useEffect(() => {
    setLoading(true);
    ApiService.apiMainRequestsList({
      ...filterValues,
      page,
      page_size: pageSize,
    })
      .then((result) => {
        setData(applyResponse<Request>(result as any));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterValues, page, pageSize, reload]);

  // Keep-alive tabs never remount, so refresh when this one is brought forward.
  useRefreshOnTabFocus(() => setReload((n) => n + 1));

  return (
    <div>
      <PageHeader
        title={t("Requests")}
        meta={
          filtered
            ? t("{{count}} matching the filter", { count: total })
            : t("{{count}} in total", { count: total })
        }
        actions={slot}
      />
      <DynamicFilterForm
        onFilter={(values) => {
          setFilterValues(values);
          resetPage();
        }}
        onExport={() =>
          exportRequestsToExcel(
            { scope: "all", filter: filterValues },
            "requests.xlsx"
          )
        }
        showStatus={false}
      />
      <ManageableTable
        tableId="requests-all"
        toolbarTarget={target}
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={paginationProps}
        rowKey={(record) => record.id}
      />
    </div>
  );
};

export default ListPage;
